import type { DonutSlice } from '@/components/ui/donut-chart';

export type RawSlice = Omit<DonutSlice, 'percent'>;

// Integer percentages that add up to exactly 100. Rounding each share on
// its own drifts to 99 or 101, which reads as a bug next to a pie, so the
// leftover points go to the buckets with the biggest cut fraction.
function largestRemainder(values: number[]): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v * 100) / total);
  const out = exact.map((v) => Math.floor(v));
  const byFraction = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  let left = 100 - out.reduce((sum, v) => sum + v, 0);
  for (const { i } of byFraction) {
    if (left <= 0) break;
    out[i] = (out[i] ?? 0) + 1;
    left -= 1;
  }
  return out;
}

// Empty buckets are dropped only AFTER the percentages are computed, so
// the denominator stays the full creator count and the fixed slice order
// is preserved for the ones that survive.
export function withPercentages(raw: RawSlice[]): DonutSlice[] {
  const percents = largestRemainder(raw.map((s) => s.value));
  return raw
    .map((slice, i) => ({ ...slice, percent: percents[i] ?? 0 }))
    .filter((slice) => slice.value > 0);
}
